/**
 * ConversationItem — single conversation row in the messages list.
 * Shows avatar, name, last message preview, time, and unread badge.
 */
import { memo, type MouseEvent } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { isGroupConversationType } from '@wlb/shared/constants';
import { getFirstName } from '@wlb/shared/utils/postDisplay';
import {
  formatUnreadBadgeCount,
  resolveConversationUnreadCount,
} from '@wlb/shared/utils/unreadMessages';
import { PinIcon, BellOffIcon, StarIcon, VerticalKebabIcon } from '../../components/ui/icons';
import { avatarFallback } from '../../components/ui/avatarFallback';
import type { Conversation } from '@wlb/shared/types/chat';
import { GroupAvatar } from './GroupAvatar';
import { resolveAvatarSrc } from './avatarSource';
import '../../components/ui/avatar.css';
import './messages.css';

interface ConversationItemProps {
  conversation: Conversation;
  onPress: (conversation: Conversation) => void;
  /** Opens the row options menu; omit to render the row without one. */
  onMenu?: (conversation: Conversation, anchorRect: DOMRect) => void;
  menuOpen?: boolean;
}

function resolveDisplayName(conversation: Conversation, t: (key: string) => string): string {
  if (isGroupConversationType(conversation.conversationType)) {
    // A group title is not a person's name, so it is never shortened.
    return conversation.groupName || conversation.name || t('chat.unnamedGroup');
  }
  return getFirstName(conversation.name) || conversation.name || t('chat.unknownUser');
}

function ConversationItemBase({ conversation, onPress, onMenu, menuOpen = false }: ConversationItemProps) {
  const { t } = useTranslation();
  const avatarSrc = resolveAvatarSrc(conversation.avatar);
  const displayName = resolveDisplayName(conversation, t);
  const isGroup = isGroupConversationType(conversation.conversationType);
  const unreadCount = resolveConversationUnreadCount(conversation);
  const unreadLabel = formatUnreadBadgeCount(unreadCount);
  const isUnread = unreadLabel !== null;
  const peerFallback = avatarFallback({
    seed: conversation.peerUserGuid ?? conversation.peerUserId ?? conversation.id,
    name: displayName,
  });
  // The row carries an aria-label, which overrides its contents, so the count
  // has to travel in the label rather than as text beside the badge.
  const rowLabel = [
    displayName,
    conversation.lastMessage || '',
    conversation.time || '',
    isUnread ? t('messagesScreen.accessibility.unreadMessages', { amount: unreadLabel }) : '',
  ]
    .filter(part => part !== '')
    .join('. ');

  const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
    onMenu?.(conversation, event.currentTarget.getBoundingClientRect());
  };

  return (
    <div className="conversation-row">
      <div
        className={`conversation-item${isUnread ? ' conversation-item--unread' : ''}`}
        onClick={() => onPress(conversation)}
        role="button"
        tabIndex={0}
        aria-label={rowLabel}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onPress(conversation);
          }
        }}
      >
        <div className="conversation-item__avatar-wrap">
          {isGroup ? (
            <GroupAvatar
              name={displayName}
              avatar={conversation.avatar}
              members={conversation.members}
              variant="list"
            />
          ) : avatarSrc ? (
            <img src={avatarSrc} className="conversation-item__avatar" alt="" aria-hidden loading="lazy" decoding="async" />
          ) : (
            <div className="conversation-item__avatar-fallback" data-avatar-tone={peerFallback.tone} aria-hidden>
              {peerFallback.initial}
            </div>
          )}
          {conversation.isOnline && <div className="conversation-item__online-dot" aria-hidden />}
        </div>

        <div className="conversation-item__body">
          <div className="conversation-item__header">
            <span className={`conversation-item__name${isGroup ? ' conversation-item__name--group' : ''}`}>
              {displayName}
            </span>
            <div className="conversation-item__status-icons">
              {conversation.isPinned && <PinIcon size={14} aria-hidden />}
              {conversation.isFavourite && <StarIcon size={14} aria-hidden />}
            </div>
          </div>

          <div className="conversation-item__preview">
            {conversation.lastMessageByMe && (
              <span className="conversation-item__preview-text">{t('messagesScreen.youPrefix')}</span>
            )}
            <span className="conversation-item__preview-text">
              {conversation.lastMessage || ''}
            </span>
          </div>
        </div>

        <div className="conversation-item__meta">
          {conversation.time && (
            <span className="conversation-item__time">{conversation.time}</span>
          )}
          <div className="conversation-item__meta-icons">
            {conversation.isMuted && <BellOffIcon size={14} aria-hidden />}
            {unreadLabel !== null && (
              <span className="conversation-item__unread-badge" aria-hidden>
                {unreadLabel}
              </span>
            )}
          </div>
        </div>
      </div>

      {onMenu && (
        <button
          type="button"
          className="conversation-row__menu"
          aria-label={t('messagesScreen.accessibility.conversationOptions', { name: displayName })}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={handleMenu}
        >
          <VerticalKebabIcon size={20} />
        </button>
      )}
    </div>
  );
}

export const ConversationItem = memo(ConversationItemBase);
